import React, { useEffect, useState } from "react";
import { t } from "i18next";
import {
    ResponsiveContainer,
    ComposedChart,
    Line,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    Cell,
} from "recharts";
import { LivisComposedChartProps } from '../types/chart-types';
import LivisResponsiveWrapper from "../wrappers/LivisResponsiveWrapper";

/**
 * LivisComposedChart - A customizable combined chart component
 * 
 * This component creates a composed chart that can display both bar and line series
 * on the same chart. It supports dual Y-axes, responsive behavior, and extensive
 * customization options for all chart elements.
 * 
 * Features:
 * - Combined bar and line chart visualization
 * - Dual Y-axis support (left and right)
 * - Responsive design with window resize handling
 * - Data validation and transformation
 * - Customizable styling for all chart components
 * - Empty state handling with custom messages
 */
const LivisComposedChart: React.FC<LivisComposedChartProps> = ({
    data,
    height = 400,
    barConfig = {
        dataKey: "value",
        fill: "#8884d8",
        yAxisId: "left"
    },
    lineConfig = {
        dataKey: "line",
        stroke: "#82ca9d",
        yAxisId: "left"
    },
    xAxisConfig = {
        dataKey: "name"
    },
    yAxisConfig = {
        left: {
            yAxisId: "left",
            label: "Value"
        }
    },
    tooltipConfig = {},
    legendConfig = {},
    gridConfig = {},
    chartMargin,
    dataTransform,
    noDataMessage = `${t("No Record Found")}`
}) => {
    // State to track window width for responsive behavior
    const [width, setWidth] = useState(0);

    /**
     * Set up responsive behavior by listening to window resize events
     * Updates width state which affects chart margins and layout
     */
    useEffect(() => {
        const handleResize = () => setWidth(window.innerWidth);
        window.addEventListener("resize", handleResize);
        handleResize(); // Set initial width
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    // Transform data if transformer function is provided
    const formattedData = dataTransform ? dataTransform(data) : data;

    /**
     * Validate and clean the chart data
     * 
     * This process:
     * 1. Filters out invalid items (missing required keys)
     * 2. Ensures numeric values for bar and line data
     * 3. Converts string numbers to actual numbers
     * 4. Sets default values for missing numeric data
     */
    const validatedData = Array.isArray(formattedData) ? formattedData.filter(item => {
        return item && item[xAxisConfig.dataKey] !== undefined;
    }).map(item => ({
        ...item,
        // Ensure numeric values for bar and line data
        ...(barConfig.dataKey && item[barConfig.dataKey] !== undefined && {
            [barConfig.dataKey]: Number(item[barConfig.dataKey]) || 0
        }),
        ...(lineConfig.dataKey && item[lineConfig.dataKey] !== undefined && {
            [lineConfig.dataKey]: Number(item[lineConfig.dataKey]) || 0
        })
    })) : [];

    // Debug logging for development environment
    if (process.env.NODE_ENV === 'development') {
        console.log("LivisComposedChart - Original data:", data);
        console.log("LivisComposedChart - Validated data:", validatedData);
        if (validatedData && validatedData.length > 0) {
            console.log("LivisComposedChart - First data item:", validatedData[0]);
        }
    }

    // Render empty state when no valid data is available
    if (!data || data.length === 0 || !validatedData || validatedData.length === 0) {
        console.log("LivisComposedChart - No valid data provided");
        return (
            <ResponsiveContainer
                width="100%"
                height={height}
                className="livis-dashboard-no-record-centered"
            >
                <p>{t(noDataMessage)}</p>
            </ResponsiveContainer>
        );
    }

    /**
     * Calculate chart margins based on screen size or use provided values
     * Smaller screens get adjusted left margin to prevent clipping
     */
    const margin = chartMargin || {
        left: width < 599 ? -25 : 0,
        bottom: 30,
        right: 0
    };

    return (
        <div style={{ width: "100%", height }}>
            <LivisResponsiveWrapper width="100%" height="100%">
                <ComposedChart
                    data={validatedData}
                    margin={margin}
                >
                    {/* Grid lines for better chart readability */}
                    <CartesianGrid
                        stroke={gridConfig.stroke || "#f5f5f5"}
                        vertical={gridConfig.vertical !== false}
                        horizontal={gridConfig.horizontal !== false}
                        strokeDasharray={gridConfig.strokeDasharray}
                    />

                    {/* X-Axis configuration for category data */}
                    <XAxis
                        dataKey={xAxisConfig.dataKey}
                        axisLine={xAxisConfig.axisLine || { stroke: "#E5E5EF", strokeWidth: 2 }}
                        tick={xAxisConfig.tick || { fontSize: 'var(--livis-font-size-xxs)' }}
                        tickMargin={xAxisConfig.tickMargin || 10}
                        label={
                            typeof xAxisConfig.label === 'string' 
                                ? {
                                    value: xAxisConfig.label,
                                    position: "insideBottom",
                                    offset: -20,
                                    style: { fontSize: 'var(--livis-font-size-xs)', fill: "#666" },
                                }
                                : xAxisConfig.label
                        }
                    />

                    {/* Tooltip for hover interactions */}
                    <Tooltip
                        content={tooltipConfig.custom && tooltipConfig.content ? tooltipConfig.content : undefined}
                        formatter={tooltipConfig.formatter}
                    />

                    {/* Legend component (shown by default unless explicitly disabled) */}
                    {(legendConfig?.show !== false) && (
                        <Legend
                            verticalAlign={legendConfig?.verticalAlign || (legendConfig?.position === 'top' ? "top" : "bottom")}
                            align={legendConfig?.align}
                            wrapperStyle={legendConfig?.wrapperStyle || legendConfig?.style}
                        />
                    )}

                    {/* Bar Component - only render if dataKey exists and has valid data */}
                    {barConfig && barConfig.dataKey && validatedData.some(item => item[barConfig.dataKey] !== undefined) && (
                        <Bar
                            dataKey={barConfig.dataKey}
                            fill={barConfig.fill || "#8884d8"}
                            barSize={barConfig.barSize || 30}
                            radius={barConfig.radius || [0, 0, 0, 0]}
                            yAxisId={barConfig.yAxisId || "left"}
                            legendType="rect"
                            name={barConfig.name || barConfig.dataKey}
                        >
                            {/* Custom colors for individual bars if enabled */}
                            {barConfig.useColors && validatedData?.map((entry: any, index: number) => (
                                <Cell key={`cell-${index}`} fill={entry.color || barConfig.fill || "#8884d8"} />
                            ))}
                        </Bar>
                    )}

                    {/* Line Component - only render if dataKey exists and has valid data */}
                    {lineConfig && lineConfig.dataKey && validatedData.some(item => item[lineConfig.dataKey] !== undefined) && (
                        <Line
                            type={lineConfig.type || "monotone"}
                            dataKey={lineConfig.dataKey}
                            stroke={lineConfig.stroke || "#82ca9d"}
                            strokeWidth={lineConfig.strokeWidth || 2}
                            dot={lineConfig.dot !== false}
                            strokeLinecap={lineConfig.strokeLinecap || "round"}
                            yAxisId={lineConfig.yAxisId || "left"}
                            legendType="line"
                            name={lineConfig.name || lineConfig.dataKey}
                        />
                    )}

                    {/* Y-Axes - Support both array and object configuration formats */}
                    {(() => {
                        /**
                         * Handle Y-axis configuration in multiple formats:
                         * 1. Array format: Direct array of axis configurations
                         * 2. Object format: Object with 'left' and 'right' properties
                         * 3. Auto-detection: Create axes based on bar/line yAxisId values
                         */
                        
                        // Check if yAxisConfig is an array
                        if (Array.isArray(yAxisConfig)) {
                            return yAxisConfig.map((axisConfig, index) => (
                                <YAxis
                                    key={`yaxis-${axisConfig.yAxisId || index}`}
                                    yAxisId={axisConfig.yAxisId}
                                    orientation={axisConfig.orientation}
                                    tickLine={axisConfig.tickLine}
                                    label={axisConfig.label}
                                />
                            ));
                        }
                        
                        // Handle object format and auto-detection
                        const axes = [];
                        
                        // Create left Y-axis if configured or if any component uses it
                        if (yAxisConfig?.left || barConfig.yAxisId === "left" || lineConfig.yAxisId === "left") {
                            axes.push(
                                <YAxis
                                    key="left"
                                    yAxisId="left"
                                    tickLine={false}
                                    label={yAxisConfig?.left?.label ? {
                                        value: yAxisConfig.left.label,
                                        angle: -90,
                                        position: "insideLeft",
                                        style: { fontSize: 'var(--livis-font-size-xs)', fill: "#666" },
                                        offset: 10
                                    } : undefined}
                                />
                            );
                        }
                        
                        // Create right Y-axis if configured or if any component uses it
                        if (yAxisConfig?.right || barConfig.yAxisId === "right" || lineConfig.yAxisId === "right") {
                            axes.push(
                                <YAxis
                                    key="right"
                                    yAxisId="right"
                                    orientation="right"
                                    tickLine={false}
                                    label={yAxisConfig?.right?.label ? {
                                        value: yAxisConfig.right.label,
                                        angle: 90,
                                        position: "insideRight",
                                        style: { fontSize: 'var(--livis-font-size-xs)', fill: "#666" },
                                        offset: 10
                                    } : undefined}
                                />
                            );
                        }
                        
                        return axes;
                    })()}
                </ComposedChart>
            </LivisResponsiveWrapper>
        </div>
    );
};

export default LivisComposedChart;